import { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faCalendarDay,
  faCalendarWeek,
  faUserTie,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import SelectDropdown from "../../../shared/components/SelectDropdown";

function Field({ label, hint, children }) {
  return (
    <div className="flex min-w-[200px] flex-1 flex-col gap-1.5">
      <label className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
        {hint && (
          <span className="font-normal normal-case tracking-normal text-gray-400">
            {hint}
          </span>
        )}
      </label>
      {children}
    </div>
  );
}

/** Label with a leading icon, so all three controls read as one family. */
function FieldLabel({ icon, children }) {
  return (
    <>
      <FontAwesomeIcon icon={icon} className="h-3 w-3 text-[#144677]" />
      {children}
    </>
  );
}

function FilterSection({ title, children, tone = "plain" }) {
  return (
    <div
      className={`flex flex-col gap-2.5 ${
        tone === "shaded"
          ? "border-t border-gray-200 bg-gray-50/70 pt-3 xl:border-l xl:border-t-0 xl:pl-4 xl:pt-0"
          : ""
      }`}
    >
      <p className="text-[11px] font-bold uppercase tracking-wider text-[#144677]">
        {title}
      </p>
      <div className="flex flex-wrap items-end gap-3">{children}</div>
    </div>
  );
}

export default function FilterBar({ filters, values, onChange, disabled }) {
  const weekEnds = filters?.week_ends;

  const yearOptions = useMemo(() => {
    const seen = [];
    (weekEnds ?? []).forEach((week) => {
      if (week.financial_year && !seen.includes(week.financial_year)) {
        seen.push(week.financial_year);
      }
    });
    return seen.map((year) => ({ value: year, label: year }));
  }, [weekEnds]);

  const weekOptions = useMemo(
    () =>
      (weekEnds ?? [])
        .filter((week) => !values.financial_year || week.financial_year === values.financial_year)
        .map((week) => ({
          value: week.week_end_date,
          label: week.label || week.week_end_date,
        })),
    [weekEnds, values.financial_year],
  );

  const billingOptions = useMemo(
    () =>
      (filters?.all_billing_companies ?? []).map((entry) => ({
        value: entry.billing_company,
        label: entry.parent_company
          ? entry.billing_company
          : `${entry.billing_company} (no parent)`,
      })),
    [filters?.all_billing_companies],
  );
  const accountManagerOptions = useMemo(
    () =>
      (filters?.account_managers ?? []).map((value) => ({
        value,
        label: value,
      })),
    [filters?.account_managers],
  );
  const referralPortfolioOptions = useMemo(
    () =>
      (filters?.referral_portfolios ?? []).map((value) => ({
        value,
        label: value,
      })),
    [filters?.referral_portfolios],
  );

  const selectedYear =
    yearOptions.find((option) => option.value === values.financial_year) ?? null;
  const selectedWeek =
    weekOptions.find((option) => option.value === values.week_end_date) ?? null;
  const selectedBilling =
    billingOptions.find((option) => option.value === values.billing_company) ?? null;
  const selectedAccountManager =
    accountManagerOptions.find((option) => option.value === values.account_manager) ?? null;
  const selectedReferralPortfolio =
    referralPortfolioOptions.find((option) => option.value === values.referral_portfolio) ?? null;

  /* Changing the year moves the report to the newest week inside it. Leaving the old
     week selected would show a week from a year the control says is not selected, and
     clearing it would leave the page with nothing to load. */
  const handleYearChange = (option) => {
    const year = option?.value ?? "";
    const firstOfYear = (weekEnds ?? []).find((week) => week.financial_year === year);
    onChange({
      financial_year: year,
      week_end_date: firstOfYear?.week_end_date ?? values.week_end_date,
    });
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white px-4 py-4 shadow-sm">
      <div className="grid gap-4 xl:grid-cols-[minmax(420px,0.8fr)_minmax(620px,1.2fr)]">
        <FilterSection title="Period">
          <Field label={<FieldLabel icon={faCalendarDay}>Financial Year</FieldLabel>}>
            <SelectDropdown
              options={yearOptions}
              value={selectedYear}
              isClearable={false}
              isDisabled={disabled || !yearOptions.length}
              placeholder="Select year..."
              onChange={handleYearChange}
            />
          </Field>

          <Field
            label={<FieldLabel icon={faCalendarWeek}>Billing Week</FieldLabel>}
            hint="(invoice date)"
          >
            <SelectDropdown
              options={weekOptions}
              value={selectedWeek}
              isClearable={false}
              isDisabled={disabled || !weekOptions.length}
              placeholder="Select billing week..."
              onChange={(option) => onChange({ week_end_date: option?.value ?? "" })}
            />
          </Field>
        </FilterSection>

        <FilterSection title="Customer">
          <Field label={<FieldLabel icon={faBuilding}>Billing Company</FieldLabel>}>
            <SelectDropdown
              options={billingOptions}
              value={selectedBilling}
              isDisabled={disabled}
              placeholder="All billing companies"
              onChange={(option) => onChange({ billing_company: option?.value ?? "" })}
            />
          </Field>

          <Field label={<FieldLabel icon={faUserTie}>Account Manager</FieldLabel>}>
            <SelectDropdown
              options={accountManagerOptions}
              value={selectedAccountManager}
              isDisabled={disabled}
              placeholder="All account managers"
              onChange={(option) => onChange({ account_manager: option?.value ?? "" })}
            />
          </Field>

          <Field label={<FieldLabel icon={faBuilding}>Customer Type</FieldLabel>}>
            <SelectDropdown
              options={referralPortfolioOptions}
              value={selectedReferralPortfolio}
              isDisabled={disabled}
              placeholder="All customer types"
              onChange={(option) => onChange({ referral_portfolio: option?.value ?? "" })}
            />
          </Field>

          {(values.billing_company || values.account_manager || values.referral_portfolio) && (
            <button
              type="button"
              onClick={() =>
                onChange({
                  billing_company: "",
                  account_manager: "",
                  referral_portfolio: "",
                })
              }
              className="flex h-10 cursor-pointer items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-500 transition-colors duration-200 hover:border-[#FF6266]/40 hover:bg-[#FF6266]/5 hover:text-[#FF6266] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#144677]/40"
            >
              <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
              Clear
            </button>
          )}
        </FilterSection>
      </div>
    </div>
  );
}
